import { useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { Button } from '@ems/ui/components/button'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { sessionQuery } from '#admin/features/auth/queries.ts'
import { hasPlatformScope } from '#admin/features/platform/api.ts'
import { isReachable } from '#admin/features/platform/tenants/api.ts'
import { NewEditionDialog } from '#admin/features/platform/tenants/new-edition-dialog.tsx'
import { provisionOptionsQuery, tenantsQuery } from '#admin/features/platform/tenants/queries.ts'
import {
  TenantsTable,
  type TenantFilters,
} from '#admin/features/platform/tenants/tenants-table.tsx'
import { TenantsSkeleton } from '#admin/features/platform/platform-skeletons.tsx'

const text = (value: unknown, max = 200) => (typeof value === 'string' ? value.slice(0, max) : '')

/**
 * `/platform/tenants` — every edition on the platform.
 *
 * The search and filters live in the URL, so "the suspended editions on the conference plan" is a
 * link somebody can paste into a support ticket, and Back returns to the same view. Provisioning
 * opens in a dialog over the list rather than sharing the page with it.
 */
export const Route = createFileRoute('/_authenticated/platform/tenants/')({
  pendingComponent: TenantsSkeleton,
  validateSearch: (search: Record<string, unknown>): TenantFilters => ({
    q: text(search['q']),
    status: text(search['status'], 20),
    plan: text(search['plan'], 40),
    series: text(search['series'], 63),
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(tenantsQuery()),
      context.queryClient.ensureQueryData(provisionOptionsQuery()),
    ])
  },
  component: TenantsPage,
})

function TenantsPage() {
  const filters = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: tenants } = useSuspenseQuery(tenantsQuery())
  const { data: options } = useSuspenseQuery(provisionOptionsQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const [open, setOpen] = useState(false)

  const canProvision = hasPlatformScope(session, 'platform:tenants.manage')
  const unreachable = tenants.items.filter((tenant) => !isReachable(tenant))

  const provisionButton = canProvision ? (
    <Button onClick={() => setOpen(true)}>
      <PlusIcon />
      Provision edition
    </Button>
  ) : null

  return (
    <>
      <PageHeader
        title="Editions"
        description="One tenant is one event edition. Open one to change its modules, plan, domains and status."
        actions={provisionButton}
      />

      {unreachable.length > 0 ? (
        // Surfaced rather than left to be discovered. These are tenants provisioned before the
        // route bound a domain: they have roles, modules and a plan, and no request can reach
        // them. Nobody would think to look for them.
        <Alert variant="destructive">
          <AlertTitle>
            {unreachable.length} edition{unreachable.length === 1 ? '' : 's'} without a verified
            hostname
          </AlertTitle>
          <AlertDescription>
            Tenant resolution is by Host. These resolve for nobody until a domain is bound:{' '}
            {unreachable.map((tenant) => tenant.slug).join(', ')}.
          </AlertDescription>
        </Alert>
      ) : null}

      <TenantsTable
        tenants={tenants.items}
        filters={filters}
        onFiltersChange={(next) =>
          void navigate({ search: (prev) => ({ ...prev, ...next }), replace: true })
        }
      />

      {canProvision ? (
        <NewEditionDialog
          open={open}
          onOpenChange={setOpen}
          options={options}
          onProvisioned={(tenant) => {
            setOpen(false)
            void navigate({ to: '/platform/tenants/$tenantId', params: { tenantId: tenant.id } })
          }}
        />
      ) : null}
    </>
  )
}
